import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { STATS } from "../mock";

function useInView(ref) {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setInView(true);
          obs.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [ref]);
  return inView;
}

function Counter({ value, suffix = "", duration = 1400 }) {
  const ref = useRef(null);
  const inView = useInView(ref);
  const [n, setN] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const start = performance.now();
    let raf = 0;
    const step = (t) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.round(eased * value));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [inView, value, duration]);

  return (
    <span ref={ref} className="font-serif-display text-4xl sm:text-5xl md:text-6xl text-white break-words">
      {n}
      <span className="text-cyan-200">{suffix}</span>
    </span>
  );
}

export default function ImpactStats() {
  return (
    <section className="relative py-24 border-t border-white/10">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <p className="text-xs uppercase tracking-[0.28em] text-cyan-200/90 mb-4">Our Impact</p>
            <h2 className="font-serif-display text-5xl lg:text-6xl font-semibold text-white leading-[1.02]">
              A chapter that <span className="hl-block-deep">shows up.</span>
            </h2>
          </div>
          <p className="text-slate-200 max-w-md font-serif-body text-lg leading-relaxed">
            Since founding in September 2024, BridgeColumbia has become a go-to coalition for open dialogue on campus.
          </p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
          {STATS.map((s, i) => {
            const cardInner = (
              <>
                <Counter value={s.value} suffix={s.suffix} />
                <p className="mt-4 font-serif-display text-lg text-white flex items-center gap-2">
                  {s.label}
                  {s.href && (
                    <ArrowUpRight className="w-4 h-4 text-cyan-200 opacity-70 group-hover:opacity-100 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" />
                  )}
                </p>
                <p className="mt-1 text-sm text-slate-300">{s.sub}</p>
                <div className="absolute -right-8 -bottom-8 w-32 h-32 rounded-full bg-cyan-300/5 blur-2xl" />
              </>
            );

            const baseCls =
              "group relative overflow-hidden rounded-2xl border border-white/15 bg-[#145074] p-6 sm:p-8 hover:border-cyan-300/50 hover:bg-[#1a618d] transition-colors block h-full";

            if (s.href) {
              return (
                <Link key={i} to={s.href} className={baseCls}>
                  {cardInner}
                </Link>
              );
            }
            return (
              <div key={i} className={baseCls}>
                {cardInner}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
